import { forwardRef, useRef, useState } from 'react';
import { DisclosureExpand } from '@sas/react-svg';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { ButtonVariant, getButtonDesignTokens } from '../../Button';
import { BaseButton } from '../../Button/__internal__/BaseButton';
import { InvisibleText } from '../../InvisibleText';
import { Menu } from '../../Menu';
import { useToggleMenu } from '../../MenuButton/useToggleMenu';
import bundle from '../NovaComponents-SplitButton-gui-icu.properties';
import cn from './BaseSplitButton.css.json';
import './BaseSplitButton.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('SplitButton');
const useTextLocalizer = createUseTextLocalizer(bundle);

/** @internal */

/** @internal */
export const BaseSplitButton = /*#__PURE__*/forwardRef(function SplitButton({
  disabled,
  onItemSelected,
  onPress,
  variant,
  tokens,
  theme,
  items,
  text,
  className,
  style,
  updateButtonAction = false,
  menuAlign,
  testId,
  focusable = true,
  autoFocus,
  ...props
}, ref) {
  const v = variant || ButtonVariant.Standard;
  const [defaultFocusIndex, setDefaultFocusIndex] = useState(-1);
  const [activeItemIndex, setActiveItemIndex] = useState();
  const [isActive, setIsActive] = useState(false);
  const localize = useTextLocalizer();
  const selfRef = useRef(null);
  const dt = useDesignTokens();
  const buttonTokens = {
    ...getSplitButtonDesignTokens(dt, {
      disabled,
      variant: v,
      tokens
    }),
    ...theme?.(dt, {
      disabled: disabled ?? false
    })
  };
  const {
    handleDismiss,
    handleMouseDown: handleMenuButtonMouseDown,
    handlePress: handleMenuButtonPress,
    setMenuIsOpen,
    menuIsOpen
  } = useToggleMenu({
    disabled,
    onDismiss: () => {
      selfRef.current?.focus();
      props.onClose?.();
    },
    onOpen: () => {
      setDefaultFocusIndex(-1);
      props.onOpen?.();
    }
  });
  if (updateButtonAction && activeItemIndex !== undefined) {
    const activeItem = items[activeItemIndex];
    text = typeof activeItem === 'string' ? activeItem : activeItem.text;
  }
  const setTruncationRef = useTruncationTooltip();
  const setKeyboardTooltipRef = useKeyboardTooltip({
    traverseChildren: true
  });
  const handleKeyDown = function (e) {
    if (!disabled && !menuIsOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsActive(true);
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setMenuIsOpen(true);
        props.onOpen?.();
        setDefaultFocusIndex(e.key === 'ArrowDown' ? 0 : items.length - 1);
      }
    }
  };
  const handleKeyUp = function (e) {
    if (!disabled && !menuIsOpen) {
      if ((e.key === 'Enter' || e.key === ' ') && isActive) {
        setIsActive(false);
        handleDefaultButtonPress();
      }
    }
  };
  const handleItemSelected = function (location) {
    const index = location[0][1];
    onItemSelected(index);
    setMenuIsOpen(false);
    props.onClose?.();
    if (updateButtonAction) {
      setActiveItemIndex(index);
    }
    selfRef.current?.focus();
  };
  const handleDefaultButtonPress = function () {
    if (updateButtonAction && activeItemIndex !== undefined) {
      onItemSelected(activeItemIndex);
    } else {
      onPress?.();
    }
  };
  const invisibleTextId = useHtmlId();
  const ariaDescribedBy = [];
  if (!disabled) {
    ariaDescribedBy.push(invisibleTextId);
  }
  if (props.ariaDescribedBy) {
    if (typeof props.ariaDescribedBy === 'string') {
      ariaDescribedBy.push(props.ariaDescribedBy);
    } else {
      ariaDescribedBy.push(...props.ariaDescribedBy);
    }
  }
  return /*#__PURE__*/_jsxs("div", {
    ref: element => {
      selfRef.current = element;
      setRef(ref, element);
      setTruncationRef(element);
      setKeyboardTooltipRef(element);
    },
    role: "group",
    "aria-roledescription": props.ariaRoleDescription === '' ? undefined : props.ariaRoleDescription ?? localize('role.aria'),
    "aria-expanded": menuIsOpen,
    "aria-haspopup": "menu",
    "aria-label": text,
    "aria-describedby": ariaDescribedBy.join(' '),
    "aria-disabled": disabled,
    "aria-current": props.ariaCurrent,
    className: cx(cn.container, className),
    "data-testid": testId,
    tabIndex: focusable ? 0 : -1,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    style: {
      ...buttonTokens,
      ...style
    },
    onBlur: () => setIsActive(false),
    "data-autofocus": autoFocus,
    onFocus: props.onFocus,
    children: [!disabled ? /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTextId,
      children: localize('button.aria')
    }) : null, /*#__PURE__*/_jsx(BaseButton, {
      className: cx(cn.button, !disabled && isActive && cn.active, props.buttonClassName),
      focusable: false,
      disabled: disabled,
      onPress: handleDefaultButtonPress,
      text: text,
      variant: variant
      // suppress the button tokens, use the buttonTokens above.
      ,
      theme: () => {
        return {
          '--button-fg': undefined,
          '--button-active-fg': undefined,
          '--button-hover-fg': undefined,
          '--button-bg': undefined,
          '--button-active-bg': undefined,
          '--button-hover-bg': undefined,
          '--button-bd': undefined,
          '--button-active-bd': undefined,
          '--button-hover-bd': undefined,
          '--button-opacity': undefined,
          '--button-active-opacity': undefined,
          '--button-hover-opacity': undefined,
          '--button-filter': undefined,
          '--button-active-filter': undefined,
          '--button-hover-filter': undefined
        };
      },
      style: props.buttonStyle,
      testId: postfixTestId(testId, 'actionButton')
    }), /*#__PURE__*/_jsx(BaseButton, {
      className: cx(cn.menu, !disabled && menuIsOpen && cn.active),
      icon: {
        icon: DisclosureExpand,
        tooltip: ''
      },
      focusable: false,
      disabled: disabled,
      variant: variant,
      onPress: handleMenuButtonPress,
      onMouseDown: handleMenuButtonMouseDown
      // suppress the button tokens, use the buttonTokens above.
      ,
      theme: () => {
        if (menuIsOpen && variant === ButtonVariant.Primary && dt.themeVariant === 'high-contrast') {
          return {
            '--button-fg': dt.tokens['color-action-text-emphasis-selected'],
            '--button-active-fg': dt.tokens['color-action-text-emphasis-selected'],
            '--button-hover-fg': dt.tokens['color-action-text-emphasis-selected'],
            '--button-bg': undefined,
            '--button-active-bg': undefined,
            '--button-hover-bg': undefined,
            '--button-bd': undefined,
            '--button-active-bd': undefined,
            '--button-hover-bd': undefined,
            '--button-opacity': undefined,
            '--button-active-opacity': undefined,
            '--button-hover-opacity': undefined,
            '--button-filter': undefined,
            '--button-active-filter': undefined,
            '--button-hover-filter': undefined
          };
        }
        if (!dt.enableNovaTokens && menuIsOpen && dt.themeVariant === 'high-contrast') {
          return {
            '--button-fg': dt.reportTokens.stateStandard.active.fg,
            '--button-active-fg': dt.reportTokens.stateStandard.active.fg,
            '--button-hover-fg': dt.reportTokens.stateStandard.active.fg,
            '--button-bg': undefined,
            '--button-active-bg': undefined,
            '--button-hover-bg': undefined,
            '--button-bd': undefined,
            '--button-active-bd': undefined,
            '--button-hover-bd': undefined,
            '--button-opacity': undefined,
            '--button-active-opacity': undefined,
            '--button-hover-opacity': undefined,
            '--button-filter': undefined,
            '--button-active-filter': undefined,
            '--button-hover-filter': undefined
          };
        }
        return {
          '--button-fg': undefined,
          '--button-active-fg': undefined,
          '--button-hover-fg': undefined,
          '--button-bg': undefined,
          '--button-active-bg': undefined,
          '--button-hover-bg': undefined,
          '--button-bd': undefined,
          '--button-active-bd': undefined,
          '--button-hover-bd': undefined,
          '--button-opacity': undefined,
          '--button-active-opacity': undefined,
          '--button-hover-opacity': undefined,
          '--button-filter': undefined,
          '--button-active-filter': undefined,
          '--button-hover-filter': undefined,
          '--split-button-divider-bd': (variant === ButtonVariant.Primary || variant === ButtonVariant.PrimarySmall) && !disabled && dt.tokens['color-action-icon-emphasis-normal']
        };
      },
      ariaLabel: localize('menubutton.aria'),
      testId: postfixTestId(testId, 'menuButton')
    }), /*#__PURE__*/_jsx(Menu, {
      isOpen: menuIsOpen,
      sections: [{
        items: items.map(item => typeof item === 'string' ? {
          text: item
        } : item)
      }],
      position: {
        dockingElement: selfRef.current,
        ...menuAlign
      },
      onItemSelected: handleItemSelected,
      onDismiss: handleDismiss,
      defaultFocusLocation: [0, defaultFocusIndex],
      theme: () => ({
        '--menu-font-family': tokens?.({}).fontFamily ?? buttonTokens['--button-fontFamily']
      })
    })]
  });
});
export const getSplitButtonDesignTokens = (dt, opts) => {
  return {
    ...getButtonDesignTokens(dt, {
      disabled: opts.disabled,
      variant: opts.variant,
      tokens: opts.tokens
    }),
    '--split-button-menu-padding': opts.variant === ButtonVariant.Small || opts.variant === ButtonVariant.PrimarySmall ? dt.tokens['space-base-xs'] : `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`
  };
};
//# sourceMappingURL=BaseSplitButton.js.map